/**
 * Noendy Header Search — MedicBlu Pattern (Vanilla JS, Light DOM)
 *
 * Custom element <noendy-header-search>:
 *   - Açma/kapama (overlay click, ESC, close button, form submit)
 *   - WAAPI animation: overlay opacity 0→1, content translateY(-headerHeight)→0
 *   - Predictive search: 280ms debounce + AbortController + section render fetch
 *   - Keyboard navigation: ArrowDown/Up sonuçlar arası gezinme
 *   - Body scroll lock (html.noendy-search-open)
 *   - Focus restore (kapanışta trigger element'e)
 *
 * Backend: /search/suggest?section_id=predictive-search (Dawn baseline section)
 */
(function () {
  if (customElements.get('noendy-header-search')) return;

  var EASING = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
  var DURATION = 200;
  var PREDICTIVE_DEBOUNCE = 280;

  function getHeaderHeightPx() {
    var root = document.documentElement;
    var raw = getComputedStyle(root).getPropertyValue('--header-height') ||
              getComputedStyle(root).getPropertyValue('--noendy-header-height') ||
              '80px';
    var parsed = parseFloat(raw);
    if (isNaN(parsed)) return 80;
    if (raw.indexOf('rem') !== -1) {
      var rootFont = parseFloat(getComputedStyle(root).fontSize) || 10;
      return parsed * rootFont;
    }
    return parsed;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  class NoendyHeaderSearch extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._lastTrigger = null;
      this._abortController = null;
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onInputChanged = this._onInputChanged.bind(this);
      this._onInputKeyDown = this._onInputKeyDown.bind(this);
      this._updateHeaderBottom = this._updateHeaderBottom.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._updateVisualViewport = this._updateVisualViewport.bind(this);
    }

    /**
     * iOS soft keyboard açıldığında visual viewport shrink olur — modal content
     * yüksekliği ve top offset'i live update. Klavye açıkken parmakla scroll
     * yapıldığında modal viewport'tan kopmaz. Kullanılan CSS variables:
     *   --noendy-vv-top: visualViewport.offsetTop (klavye ile birlikte scroll)
     *   --noendy-vv-height: visualViewport.height (klavye sonrası kalan alan)
     */
    _updateVisualViewport() {
      if (!window.visualViewport) return;
      var vv = window.visualViewport;
      var root = document.documentElement;
      root.style.setProperty('--noendy-vv-top', vv.offsetTop + 'px');
      root.style.setProperty('--noendy-vv-height', vv.height + 'px');
    }

    /**
     * Document-level outside click — overlay header'ı kapatmadığı için
     * header alanına (hamburger, logo, cart) tıklayınca da modal kapansın.
     * Trigger'lar pas geçilir (kendi toggle handler'larıyla yönetilir).
     */
    _onDocumentClick(event) {
      if (!this._open) return;
      // Tıklama modal içindeyse pas geç (input, results, close button, overlay)
      if (this.contains(event.target)) return;
      // Tıklama bir trigger'sa pas geç (zaten toggle handler çalışıyor)
      if (event.target.closest('[aria-controls="' + this.id + '"]')) return;
      // Dışarı tıklandı → kapat
      this.close();
    }

    /**
     * `--noendy-header-bottom` CSS variable — header'ın getBoundingClientRect().bottom
     * değerini live yansıtır. Sticky header padding/shrink/scroll değişimlerine adapte.
     * MedicBlu'da `--sticky-area-height` CSS variable ile aynı amaç.
     */
    _updateHeaderBottom() {
      var header = document.querySelector('.section-header') || document.querySelector('.header-wrapper') || document.querySelector('header');
      if (!header) return;
      var bottom = header.getBoundingClientRect().bottom;
      if (bottom > 0) {
        document.documentElement.style.setProperty('--noendy-header-bottom', bottom + 'px');
      }
    }

    connectedCallback() {
      this.overlay = this.querySelector('.noendy-header-search__overlay');
      this.content = this.querySelector('.noendy-header-search__content');
      this.input = this.querySelector('.noendy-header-search__input');
      this.results = this.querySelector('.noendy-predictive-search');
      this.statusEl = this.querySelector('.noendy-predictive-search__status');
      this.form = this.querySelector('.noendy-header-search__form');

      var self = this;
      var modalId = this.id;

      // Trigger binding (toggle open/close)
      this._triggers = Array.prototype.slice.call(
        document.querySelectorAll('[aria-controls="' + modalId + '"]')
      );
      this._triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function (event) {
          event.preventDefault();
          if (self._open) self.close();
          else self.open(trigger);
        });
      });

      // Close: overlay click + close button — both have data-noendy-search-close
      this.querySelectorAll('[data-noendy-search-close]').forEach(function (el) {
        el.addEventListener('click', function (event) {
          event.preventDefault();
          self.close();
        });
      });

      // ESC keyboard
      document.addEventListener('keydown', this._onKeyDown);

      // Form submit → close without animation (sayfa değiştirileceği için)
      if (this.form) {
        this.form.addEventListener('submit', function () {
          self.close({ skipAnimation: true });
        });
      }

      // Predictive search input — debounced fetch + keyboard navigation
      if (this.input && this.results) {
        this.input.addEventListener('input', debounce(this._onInputChanged, PREDICTIVE_DEBOUNCE));
        this.input.addEventListener('keydown', this._onInputKeyDown);
      }

      // Header bottom live tracking (sticky header shrink/scroll değişimlerine adapte)
      this._updateHeaderBottom();
      window.addEventListener('resize', this._updateHeaderBottom, { passive: true });
      window.addEventListener('scroll', this._updateHeaderBottom, { passive: true });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener('click', this._onDocumentClick, true);
      window.removeEventListener('resize', this._updateHeaderBottom);
      window.removeEventListener('scroll', this._updateHeaderBottom);
      if (this._abortController) this._abortController.abort();
    }

    _onKeyDown(event) {
      if (event.key === 'Escape' && this._open) {
        event.preventDefault();
        this.close();
      }
    }

    _onInputKeyDown(event) {
      if (!this.results || this.results.hidden) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this._focusResult(0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this._focusResult(-1);
      }
    }

    _focusResult(index) {
      var items = this.results.querySelectorAll('a[href]');
      if (!items.length) return;
      var target = index < 0 ? items[items.length - 1] : items[index];
      if (target && typeof target.focus === 'function') target.focus();
    }

    open(triggerEl) {
      if (this._open) return;
      this._open = true;
      this._lastTrigger = triggerEl || null;
      // Header bottom'u açılmadan tam güncelle (sticky header değişimleri için)
      this._updateHeaderBottom();
      this.removeAttribute('hidden');
      // Force reflow so animation plays from start frame
      void this.offsetWidth;
      this.setAttribute('open', '');
      document.documentElement.classList.add('noendy-search-open');
      this._triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });

      var headerHeight = getHeaderHeightPx();

      if (this.overlay && this.overlay.animate) {
        this.overlay.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: DURATION, easing: EASING, fill: 'forwards' }
        );
      }

      if (this.content && this.content.animate) {
        this.content.animate(
          [
            { opacity: 0, transform: 'translateY(-' + headerHeight + 'px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          { duration: DURATION, easing: EASING, fill: 'forwards' }
        );
      }

      var input = this.input;
      if (input) {
        setTimeout(function () {
          input.focus();
          input.select();
        }, 60);
      }

      // Document outside-click listener — sadece search AÇIKKEN aktif.
      // setTimeout(0) ile trigger click event'inin completion'ından sonra attach
      // (aksi halde trigger click'i bu listener tetikleyip hemen close ederdi).
      var self = this;
      setTimeout(function () {
        document.addEventListener('click', self._onDocumentClick, true);
      }, 0);

      // Visual viewport listener — klavye açıkken modal pozisyonu adapt
      if (window.visualViewport) {
        this._updateVisualViewport();
        window.visualViewport.addEventListener('resize', this._updateVisualViewport);
        window.visualViewport.addEventListener('scroll', this._updateVisualViewport);
      }
    }

    close(options) {
      if (!this._open) return;
      this._open = false;
      options = options || {};

      // Document outside-click listener detach
      document.removeEventListener('click', this._onDocumentClick, true);

      // Visual viewport listener cleanup
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this._updateVisualViewport);
        window.visualViewport.removeEventListener('scroll', this._updateVisualViewport);
      }
      document.documentElement.style.removeProperty('--noendy-vv-top');
      document.documentElement.style.removeProperty('--noendy-vv-height');

      var self = this;
      var headerHeight = getHeaderHeightPx();

      this._triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

      // Predictive results gizle
      if (this.results) {
        this.results.hidden = true;
      }
      if (this.input) {
        this.input.setAttribute('aria-expanded', 'false');
      }

      var done = function () {
        self.removeAttribute('open');
        self.setAttribute('hidden', '');
        document.documentElement.classList.remove('noendy-search-open');
        if (self._lastTrigger && typeof self._lastTrigger.focus === 'function') {
          try { self._lastTrigger.focus(); } catch (e) {}
        }
      };

      if (options.skipAnimation || !this.content || !this.content.animate) {
        done();
        return;
      }

      var overlayAnim = this.overlay && this.overlay.animate
        ? this.overlay.animate(
            [{ opacity: 1 }, { opacity: 0 }],
            { duration: DURATION, easing: EASING, fill: 'forwards' }
          )
        : null;

      var contentAnim = this.content.animate(
        [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: 'translateY(-' + headerHeight + 'px)' }
        ],
        { duration: DURATION, easing: EASING, fill: 'forwards' }
      );

      var pending = [];
      if (overlayAnim && overlayAnim.finished) pending.push(overlayAnim.finished);
      if (contentAnim && contentAnim.finished) pending.push(contentAnim.finished);

      if (pending.length === 0) { done(); return; }

      Promise.all(pending).then(done).catch(done);
    }

    // ===== Predictive search =====

    _onInputChanged() {
      var query = (this.input.value || '').trim();
      if (!query) {
        this._clearResults();
        return;
      }
      this._predictiveFetch(query);
    }

    _clearResults() {
      if (this._abortController) this._abortController.abort();
      if (this.results) {
        this.results.replaceChildren();
        this.results.hidden = true;
      }
      if (this.input) {
        this.input.setAttribute('aria-expanded', 'false');
      }
      if (this.statusEl) {
        this.statusEl.textContent = '';
      }
    }

    _predictiveFetch(query) {
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();
      var signal = this._abortController.signal;
      var self = this;

      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var endpoint = root.replace(/\/$/, '') + '/search/suggest';
      var params = 'q=' + encodeURIComponent(query)
                 + '&section_id=predictive-search'
                 + '&resources[limit]=10'
                 + '&resources[limit_scope]=each';

      fetch(endpoint + '?' + params, { signal: signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search request failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          self._renderResults(html);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          // Network/parse error — sessizce log
          if (window.console) console.error('[noendy-search] predictive error:', err);
        });
    }

    _renderResults(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var section = doc.querySelector('#shopify-section-predictive-search')
                  || doc.querySelector('.shopify-section')
                  || doc.body;
      if (!section || !this.results) return;

      // Clone section'ın inner children'larını results container'a inject
      this.results.replaceChildren();
      var children = Array.prototype.slice.call(section.children);
      var self = this;
      children.forEach(function (child) {
        self.results.appendChild(child.cloneNode(true));
      });

      var anyResults = !!this.results.querySelector('a[href]');
      this.results.hidden = !anyResults && !this.results.querySelector('.predictive-search__no-results');

      if (this.input) {
        this.input.setAttribute('aria-expanded', !this.results.hidden ? 'true' : 'false');
      }
      if (this.statusEl) {
        var count = this.results.querySelectorAll('a[href]').length;
        this.statusEl.textContent = count > 0
          ? count + ' sonuç'
          : (this.results.querySelector('.predictive-search__no-results') ? 'Sonuç bulunamadı' : '');
      }
    }
  }

  customElements.define('noendy-header-search', NoendyHeaderSearch);
})();
